import React from 'react'
import { BigNumber } from '@ethersproject/bignumber'
import { PairImage, RightChevron } from '../../common'
import { PairsItem, Borders, FontWeights, Colors, INTEGRAL_SYMBOL } from '../../constants'
import { formatCurrency } from '../../model'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { LpRewards } from '../../providers/apiTokenRewards/model'
import { usePair } from '../../hooks'
import { BN_10_18 } from '../../utils'

interface Props {
  pair: PairsItem
  liquidityBalance: BigNumber
  lpRewards?: LpRewards
}

export function LiquidityItem({ pair, liquidityBalance, lpRewards }: Props) {
  const [currency, other] = pair.tokens
  const { pair: p } = usePair(currency, other)

  const totalLiquiditySupply = (p && p['state'].totalSupply) ?? BigNumber.from(0)
  const currencyReserve = (p && p.getFirstReserve().value) ?? BigNumber.from(0)
  const otherReserve = (p && p.getSecondReserve().value) ?? BigNumber.from(0)
  const liquidityBalanceIsZero = liquidityBalance.isZero() || totalLiquiditySupply.isZero()

  return (
    <ListItem>
      <PoolLink to={{ pathname: `/withdraw/${pair.address}` }}>
        <Pair>
          <PairImage currencySymbol={currency.symbol} otherSymbol={other.symbol} />
          {currency.symbol}-{other.symbol}
          <RightChevronStyled />
        </Pair>
        <PairInfo>
          <ul>
            <li>
              <Label>Pool %</Label>
              <Value>
                {' '}
                {liquidityBalanceIsZero
                  ? '-'
                  : `${formatCurrency(18, liquidityBalance.mul(BN_10_18).mul(100).div(totalLiquiditySupply), 2)}%`}
              </Value>
            </li>
            <li>
              <Label>{currency.symbol}</Label>
              <Value>
                {liquidityBalanceIsZero || currencyReserve.isZero()
                  ? '-'
                  : formatCurrency(
                      currency.decimals,
                      liquidityBalance.mul(BN_10_18).mul(currencyReserve).div(totalLiquiditySupply).div(BN_10_18),
                      6
                    )}
              </Value>
            </li>
            <li>
              <Label>{other.symbol}</Label>
              <Value>
                {liquidityBalanceIsZero || otherReserve.isZero()
                  ? '-'
                  : formatCurrency(
                      other.decimals,
                      liquidityBalance.mul(BN_10_18).mul(otherReserve).div(totalLiquiditySupply).div(BN_10_18),
                      6
                    )}
              </Value>
            </li>
            <li>
              <Label>LP Tokens</Label>
              <Value>{formatCurrency(18, liquidityBalance, 6)}</Value>
            </li>
            <li>
              <Label>Accrued Staking Rewards</Label>
              <Value>
                {lpRewards ? formatCurrency(18, lpRewards.tokenRewards.total, 2) : '-'} {INTEGRAL_SYMBOL}
              </Value>
            </li>
          </ul>
        </PairInfo>
      </PoolLink>
    </ListItem>
  )
}

const ListItem = styled.li`
  margin-bottom: 12px;

  a {
    color: ${Colors.Black};
  }
`

const PoolLink = styled(Link)`
  margin: 20px;
  padding: 15px 10px;
  display: block;
  text-decoration: none;
  background: rgb(239, 239, 239);
  background: linear-gradient(0deg, rgba(239, 239, 239, 1) 0%, rgba(238, 238, 238, 1) 100%);
  border: 1px solid ${Borders.Color};
  border-radius: ${Borders.Radius};

  &:hover {
    color: ${Colors.Hover};
    border-color: ${Colors.Hover};
  }
`

const Pair = styled.div`
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: bold;
`

const PairInfo = styled.div`
  margin-top: 15px;
  font-size: 11px;
  text-transform: uppercase;

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    margin-bottom: 2px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const Label = styled.strong`
  margin-right: 5px;
  display: flex;
  align-items: center;
  color: ${Colors.DarkGray};
  font-size: 11px;
  font-weight: ${FontWeights.Bold};
  text-transform: uppercase;
`

const Value = styled.span`
  display: block;
  color: ${Colors.DarkerGray};
  font-size: 11px;
`

const RightChevronStyled = styled(RightChevron)`
  margin-left: auto;
  height: 14px;
`
